import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { getAccessToken } from './auth';

const FILE_TRANSFER_MAX_RETRIES = 2;

// Retry only transient failures; deterministic errors (eg. 404/403) never heal
const retryTransientErrorsOnly = {
  retryCondition: (error: unknown, _args: unknown, { attempt }: { attempt: number }): boolean => {
    const status = (error as FetchBaseQueryError | undefined)?.status;
    return (
      attempt <= FILE_TRANSFER_MAX_RETRIES &&
      (status === 'FETCH_ERROR' ||
        status === 'TIMEOUT_ERROR' ||
        (typeof status === 'number' && status >= 500))
    );
  },
};

// Cognito API for authenticated requests to the backend
export const cognitoApi = createApi({
  reducerPath: 'cognitoApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BACKEND_URI,
    prepareHeaders: async headers => {
      // Attach the token when we have one; when signed out (or refresh
      // failed) omit the header entirely rather than sending
      // "Bearer undefined"
      const accessToken = await getAccessToken();
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Users',
    'Tutorials',
    'Students',
    'Staff',
    'StudentActivityLog',
    'Videos',
    'VideoCategories',
    'VideoPermissions',
    'Leads',
    'Permissions',
    'PermissionGroups',
    'Prices',
    'Notes',
    'LegacyExamMarkingRequests',
    'NotesCategories',
    'NotesPermissions',
    'LegacyExams',
    'LegacyExamTypes',
    'LegacyExamQuestions',
    'LegacyExamAttempts',
    'LegacyExamAttemptsByMarker',
    'Rooms',
    'Schools',
    'Classes',
    'UserNotes',
    'LessonEnrolments',
    'LegacyTopics',
    'LegacyTopicGroups',
    'StudentAnalytics',
    'Courses',
    'Streams',
    'Lessons',
    'LessonTemplates',
    'QuestionSets',
    'QuestionSetCandidates',
    'QuestionEnrichment',
    'QuestionTree',
    'QuestionContent',
    'SourceDocuments',
    'AttemptScans',
    'AttemptSubmissions',
  ],
  endpoints: () => ({}),
});

// General API for external services like S3, CloudFront, etc.
// This API doesn't require authentication headers like the Cognito API
export const generalApi = createApi({
  reducerPath: 'generalApi',
  // Endpoints opt into retries via extraOptions; everything else is single-shot
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: '', // No base URL since we'll use full URLs
    }),
    { maxRetries: 0 }
  ),
  tagTypes: [],
  endpoints: builder => ({
    // GET a PDF file from an external URL
    getPdf: builder.query<string | undefined, { url: string }>({
      query: ({ url }) => ({
        url,
        method: 'GET',
        responseHandler: async (response: Response) => {
          // Don't fabricate a PDF blob from an error/HTML body (e.g. an S3 404).
          // fetchBaseQuery still flags the query as errored when !response.ok.
          if (!response.ok) {
            return undefined;
          }
          const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
          // Convert blob to object URL immediately and return the URL
          return URL.createObjectURL(blob);
        },
      }),
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload a PDF file to an external URL
    putPdf: builder.mutation<void, { url: string; pdf: Uint8Array | File }>({
      query: ({ url, pdf }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/pdf',
        },
        body: pdf,
      }),
      // A paper's worth of annotations is at stake, so ride out network blips
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload a file to an external URL as the type named, which a browser cannot always tell
    putFile: builder.mutation<void, { url: string; file: Blob; contentType: string }>({
      query: ({ url, file, contentType }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: file,
      }),
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload an image file to an external URL
    putImage: builder.mutation<void, { url: string; image: File }>({
      query: ({ url, image }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': image.type,
        },
        body: image,
      }),
    }),
  }),
});

export const {
  useGetPdfQuery,
  useLazyGetPdfQuery,
  usePutFileMutation,
  usePutPdfMutation,
  usePutImageMutation,
} = generalApi;
